import React, { useMemo, useEffect, useState } from 'react';
import { LayoutGrid, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useDiscardGuard } from '../hooks/useDiscardGuard';

import { App } from '../types/api';
import { apiService } from '../services/api';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { usePermissions } from '../hooks/usePermissions';
import AppDetailsModal from '../components/AppDetailsModal';
import { ColumnPicker } from '../components/ColumnPicker';
import { CredentialField } from '../components/CredentialField';
import { DataTable, DataTableColumn, SearchInput, SortState, hasListError, isListRetrying, listTableProps } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { ListEmptyState } from '../components/ListEmptyState';
import { ErrorState } from '../components/ErrorState';
import { Field } from '../components/Field';
import { PageHeader } from '../components/PageHeader';
import { DialogActions, DialogForm } from '../components/DialogActions';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { EMPTY_VALUE, formatDays, formatDuration, formatNumber } from '../lib/formatters';
import { DateCell } from '../components/Timestamp';
import { classifyError, humanizeError } from '../lib/errors';
import { FormError } from '../components/FormError';
import { validateAppName } from '../lib/validation';
import { routeCopy } from '../routes';

// Keep these strings identical to the /my-apps entry in src/routes.ts.
const { title: PAGE_TITLE, description: PAGE_DESCRIPTION } = routeCopy('/my-apps');

type SortBy =
  | 'name'
  | 'createdAt'
  | 'updatedAt'
  | 'accessTokenExpiration'
  | 'idTokenExpiration'
  | 'refreshTokenExpiration'
  | 'authFlowSessionDuration'
  | 'enableTokenRevocation';

const SORTABLE_KEYS: ReadonlyArray<SortBy> = [
  'name',
  'createdAt',
  'updatedAt',
  'accessTokenExpiration',
  'idTokenExpiration',
  'refreshTokenExpiration',
  'authFlowSessionDuration',
  'enableTokenRevocation',
];

/** Reproduces the order GET /apps has always returned rows in. */
const DEFAULT_SORT: SortState = { column: 'createdAt', direction: 'desc' };

/**
 * `useColumnVisibility` speaks `SortState` (column: string) because it has to
 * cope with whatever columns a page declares; `useInfiniteList<App, SortBy>` wants
 * the narrowed union. Narrow through the same whitelist the query validates
 * against rather than casting.
 */
const toSortBy = (sort: SortState): { column: SortBy; direction: 'asc' | 'desc' } => ({
  column: (SORTABLE_KEYS as readonly string[]).includes(sort.column)
    ? (sort.column as SortBy)
    : 'createdAt',
  direction: sort.direction,
});

/** Absent timestamps (and unparseable ones) sort last — see queryInMemoryWindow. */
const timestamp = (value: string | null | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// Durations read exactly as App details reads them ("1 hour 30 minutes"):
// one formatter in lib/formatters, never rounded.
const minutesCell = (value: number | undefined): string => formatDuration(value);

const daysCell = (value: number | undefined): string => formatDays(value);

/**
 * How many subscriptions a credential is holding open, once we know.
 *
 * Three states, and the first one is the point of the component: the number
 * arrives on a request of its own, after the table is already on screen, so the
 * cell has to occupy its space and say "counting" without shifting the row when
 * the answer lands. A pulsing bar the width of a two-digit number does that; the
 * value then fades up into the same box.
 *
 * `animate-in` comes from tailwindcss-animate, already used by every overlay in
 * the kit, and the global prefers-reduced-motion guard in index.css flattens it
 * to nothing for anyone who asks.
 */
const SubscriptionCountCell: React.FC<{ count: number | undefined; failed?: boolean }> = ({
  count,
  failed = false,
}) => {
  // A failed count is unknown, not zero and not still counting: no pulse.
  if (failed) {
    return (
      <span className="text-muted-foreground">
        {EMPTY_VALUE}
        <span className="ml-2 text-xs">Couldn&apos;t count subscriptions</span>
      </span>
    );
  }
  if (count === undefined) {
    // Background enrichment: hidden text in the cell, never a live region —
    // one role=status per row was a live region per mounted row, and an
    // aria-label on an empty span is read unreliably (DESIGN_SYSTEM.md §8.4).
    return (
      <span className="inline-flex h-3.5 w-6 align-middle">
        <span aria-hidden="true" className="inline-block h-3.5 w-6 animate-pulse rounded bg-muted" />
        <span className="sr-only">Counting…</span>
      </span>
    );
  }

  return (
    <span
      // Keyed on the value so a later refresh re-runs the entrance rather than
      // swapping the digits in place.
      key={count}
      className="inline-block tabular-nums animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out"
    >
      {/* Zero is a known count, so it reads "0"; the em dash is only for unknown. */}
      {count === 0
        ? <span className="text-muted-foreground">{formatNumber(0)}</span>
        : <span className="font-medium">{formatNumber(count)}</span>}
    </span>
  );
};

const MyApps: React.FC = () => {
  const { accessToken } = useUser();
  const {
    selectedOrganization,
    refreshOrganizationApps,
    isLoading: orgLoading,
    appsError,
    appsLoading,
    addAppToOrganization,
  } = useOrganization();

  const organization = selectedOrganization;
  const { hasPermission } = usePermissions();
  // POST /apps, rename and GET /apps/{id}/credentials check app_clients:write;
  // DELETE /apps/{id} checks app_clients:delete (api/src/appClients.ts).
  const canWriteApps = hasPermission('app_clients:write');
  const canDeleteApps = hasPermission('app_clients:delete');

  const openAppDetails = (app: App, organizationId: string) => {
    setSelectedApp({ app, organizationId });
  };

  /**
   * This is the only page that shows the Cognito-derived columns (token
   * lifetimes, revocation), so it is the only place that pays for them.
   *
   * The shared OrganizationContext load deliberately omits them — it runs on
   * sign-in and on every organization switch regardless of route, and the API
   * answered it with one Cognito call per app client. Re-requesting here fills
   * the same context state, so the table gets its values and every other page
   * keeps the cheap read. Keyed on the organization id, and `refreshOrganizationApps`
   * is a background refresh, so this does not blank the page while it runs.
   */
  useEffect(() => {
    if (!organization?.id || !accessToken) {
      return;
    }
    void refreshOrganizationApps(organization.id, { includeCognitoDetails: true });
  }, [organization?.id, accessToken, refreshOrganizationApps]);

  /**
   * Active subscriptions per credential, on a request of its own.
   *
   * DELIBERATELY NOT part of the load above. The count answers "what is this
   * credential running, and can I delete it?" — worth showing, never worth
   * making the table wait for. It is also the number behind the 409 that
   * refuses to delete a credential still holding subscriptions, so a user can
   * see the obstacle before they meet it.
   *
   * A failure is contained, not silent: the cell stops pulsing and reads
   * "— Couldn't count subscriptions" (never 0, never an endless placeholder),
   * and every other thing on the page still works. No page-level banner.
   */
  const [subscriptionCounts, setSubscriptionCounts] = useState<Map<string, number> | null>(null);
  const [countsFailed, setCountsFailed] = useState(false);

  useEffect(() => {
    if (!organization?.id || !accessToken) return;

    const controller = new AbortController();
    setSubscriptionCounts(null);
    setCountsFailed(false);

    apiService
      .getOrganizationAppClients(organization.id, accessToken, {
        includeSubscriptionCounts: true,
        signal: controller.signal,
      })
      .then((apps) => {
        if (controller.signal.aborted) return;
        setSubscriptionCounts(new Map(apps.map((app) => [app.id, app.subscriptionCount ?? 0])));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load subscription counts:', err);
        setCountsFailed(true);
      });

    return () => controller.abort();
  }, [organization?.id, accessToken]);


  // Inline create panel above the table
  const [isCreating, setIsCreating] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  /** A create failure that is not about the name: pinned above the footer. */
  const [createSubmitError, setCreateSubmitError] = useState<string | null>(null);
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
  /**
   * The create response already carries the secret, so it is held here and
   * the credential stays on screen after the row lands in the table, ready to
   * copy. It is not the only way to read it: the app's details fetch it again
   * on demand (GET /apps/{id}/credentials, behind app_clients:write). The
   * table itself never shows a secret (that would be one request per row).
   */
  const [createdApp, setCreatedApp] = useState<App | null>(null);

  // Details modal
  const [selectedApp, setSelectedApp] = useState<{ app: App; organizationId: string } | null>(null);


  const allColumns: Array<DataTableColumn<App>> = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      className: 'max-w-[260px] truncate font-medium',
      render: (app) =>
        app.detailsUnavailable ? (
          <div className="min-w-0">
            <span className="block truncate">{app.name}</span>
            {/* Cognito was unreachable for this row, so every token column is
                UNKNOWN rather than unset — say so instead of showing four
                indistinguishable em dashes. */}
            <span className="block text-xs font-normal text-muted-foreground">
              Details unavailable
            </span>
          </div>
        ) : (
          app.name
        ),
    },
    {
      key: 'clientId',
      header: 'Client ID',
      label: 'Client ID',
      hideable: true,
      hideBelow: 'md',
      className: 'max-w-[220px] truncate font-mono text-xs text-muted-foreground',
      render: (app) => app.clientId,
    },
    {
      key: 'subscriptionCount',
      header: 'Subscriptions',
      label: 'Subscriptions',
      hideable: true,
      align: 'right',
      // Not sortable: the values arrive after the rows, so a sort on this
      // column would reorder the table under the user as the counts land.
      render: (app) => <SubscriptionCountCell count={subscriptionCounts?.get(app.id)} failed={countsFailed} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      label: 'Created',
      hideable: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (app) => <DateCell value={app.createdAt} />,
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      label: 'Updated',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (app) => <DateCell value={app.updatedAt} />,
    },
    {
      key: 'accessTokenExpiration',
      header: 'Access token',
      label: 'Access token',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      align: 'right',
      render: (app) => minutesCell(app.accessTokenExpiration),
    },
    {
      key: 'idTokenExpiration',
      header: 'ID token',
      label: 'ID token',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      align: 'right',
      render: (app) => minutesCell(app.idTokenExpiration),
    },
    {
      key: 'refreshTokenExpiration',
      header: 'Refresh token',
      label: 'Refresh token',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      align: 'right',
      render: (app) => daysCell(app.refreshTokenExpiration),
    },
    {
      key: 'authFlowSessionDuration',
      header: 'Auth session',
      label: 'Auth session',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      align: 'right',
      render: (app) => minutesCell(app.authFlowSessionDuration),
    },
    {
      key: 'enableTokenRevocation',
      header: 'Token revocation',
      label: 'Token revocation',
      hideable: true,
      defaultHidden: true,
      sortable: true,
      render: (app) =>
        app.enableTokenRevocation === undefined ? (
          EMPTY_VALUE
        ) : (
          // A configuration flag is plain text, never a dotted runtime badge;
          // same words as the App client details dialog.
          app.enableTokenRevocation ? 'Enabled' : 'Disabled'
        ),
    },
    // No actions column: DataTable renders the row's Info "Open {name}" button
    // from onOpenRow. Renaming is a field inside the record dialog.
  ];

  const columnVisibility = useColumnVisibility<App>({
    tableId: 'my-apps',
    columns: allColumns,
    defaultSort: DEFAULT_SORT,
  });

  /**
   * This page never fetches a list. `GET /apps` takes no list parameters and
   * OrganizationContext already owns the array, so this is a memory-source
   * list over it: it reveals 50 rows at a time, and `sourceKey` makes a
   * context refresh re-read the rows without losing the reader's depth,
   * search or sort. Writes patch, pin or remove the row (ruling 20).
   */
  /**
   * Publisher credentials are excluded. Each one belongs to a processor
   * template, is created and destroyed with it, and is managed from
   * /my-processor-templates — listing them here mixed two lifecycles in one
   * table and offered actions (rename, delete) the API refuses on them.
   *
   * Filtered HERE and not in OrganizationContext: AppRequestBuilder reads the
   * same `organization.apps` array to pick a client for a subscription, and
   * narrowing it centrally would silently change that page too.
   */
  const ownApps = useMemo(
    () => (organization?.apps ?? []).filter((app) => app.clientKind !== 'template_publisher'),
    [organization?.apps],
  );

  const appsQuery = useInfiniteList<App, SortBy>({
    source: 'memory',
    load: () => ownApps,
    memory: {
      searchFields: (app) => [app.name, app.clientId],
      sortAccessors: {
        name: (app) => app.name,
        createdAt: (app) => timestamp(app.createdAt),
        updatedAt: (app) => timestamp(app.updatedAt),
        accessTokenExpiration: (app) => app.accessTokenExpiration,
        idTokenExpiration: (app) => app.idTokenExpiration,
        refreshTokenExpiration: (app) => app.refreshTokenExpiration,
        authFlowSessionDuration: (app) => app.authFlowSessionDuration,
        enableTokenRevocation: (app) => app.enableTokenRevocation,
      },
      tiebreak: (app) => app.id,
    },
    rowKey: (app) => app.id,
    enabled: Boolean(organization && accessToken),
    resetKey: organization?.id ?? null,
    sourceKey: ownApps,
    initialSort: toSortBy(columnVisibility.initialSort),
    sortColumns: SORTABLE_KEYS,
    urlState: true,
    errorContext: "Couldn't load app clients",
  });

  /**
   * Renames one app, for the details dialog's own field.
   *
   * Throws rather than toasting a failure: the dialog holds the field the
   * error belongs to, and a message under it beats one that floats away.
   */
  const renameApp = async (app: App, name: string) => {
    if (!organization) return;
    if (!accessToken) throw new Error('Missing OIDC access token. Please sign in again.');
    await apiService.updateAppName(app.id, name, accessToken);
    // The row is patched from the name that was saved, marked Updated. The
    // context refresh is a background re-read of the source; it must never turn
    // a rename that landed into "Couldn't rename the app".
    appsQuery.patchItem(app.id, (row) => ({ ...row, name }), { mark: 'updated' });
    try {
      await refreshOrganizationApps(organization.id, { includeCognitoDetails: true });
    } catch (err: unknown) {
      console.error('The app clients refresh after a rename failed:', err);
    }
    toast.success('App client renamed');
  };

  const startCreatingApp = () => {
    if (!organization) return;
    setNewAppName('');
    setCreateError(null);
    setCreatedApp(null);
    setIsCreating(true);
  };

  const cancelCreatingApp = () => {
    setIsCreating(false);
    setNewAppName('');
    setCreateError(null);
    setCreateSubmitError(null);
  };

  // A typed name is never dropped by a stray Escape. The result phase holds no
  // input, so it is never guarded.
  const createGuard = useDiscardGuard({
    isDirty: isCreating && !createdApp && newAppName.trim() !== '',
    isBusy: isSubmittingCreate,
    onClose: cancelCreatingApp,
    recordNoun: 'the new app client',
  });

  const createApp = async () => {
    if (!organization) return;

    const normalizedName = newAppName.trim();
    const validationError = validateAppName(normalizedName);
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setIsSubmittingCreate(true);
    setCreateSubmitError(null);
    try {
      if (!accessToken) {
        throw new Error('Missing OIDC access token. Please sign in again.');
      }
      const newApp = await apiService.createApp(organization.id, normalizedName, accessToken);

      // Add the new app to the organization directly without a full refresh,
      // and pin it first marked New so it never lands past unrevealed rows.
      addAppToOrganization(organization.id, newApp);
      appsQuery.prependItem(newApp, { mark: 'new' });

      // No toast: the result phase below is the acknowledgement, and feedback
      // uses exactly one channel.
      setIsCreating(false);
      setNewAppName('');
      setCreateError(null);
      setCreatedApp(newApp);
    } catch (err: unknown) {
      console.error('Failed to create app:', err);
      // The dialog stays open, so the failure stays in it: under the name when
      // the refusal is about the name, above the footer otherwise. Never a toast.
      const failure = classifyError(err);
      if (failure.kind === 'conflict' && failure.field === 'name') {
        setCreateError('An app client with this name already exists. Choose a different name.');
      } else {
        setCreateSubmitError(humanizeError(err, "Couldn't create the app client"));
      }
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  const handleAppDelete = async () => {
    if (!selectedApp) return;
    if (!accessToken) {
      throw new Error('Missing OIDC access token. Please sign in again.');
    }
    const target = selectedApp;
    await apiService.deleteApp(target.app.id, accessToken);
    // The delete succeeded: acknowledge it and close both layers NOW, in the
    // same tick as removeItem, so DataTable can move focus to the replacing
    // row (it skips focus while a dialog is still open). The context's app
    // cache (other pages count and pick from it) is re-read in the background;
    // a failure there is the list's own error, never this delete's.
    appsQuery.removeItem(target.app.id);
    toast.success('App client deleted');
    setSelectedApp(null);
    void refreshOrganizationApps(target.organizationId, { includeCognitoDetails: true });
  };


  if (orgLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="shrink-0">
          <PageHeader title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
        </div>
        <DataTable
          columns={columnVisibility.columns}
          rows={[]}
          rowKey={(app) => app.id}
          itemNoun="app clients"
          listLabel="App clients"
          listState="initial"
        />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
        <EmptyState
          icon={LayoutGrid}
          title="No organization selected"
          description="Select an organization from the switcher in the sidebar to see its app clients."
        />
      </div>
    );
  }

  /*
   * This page owns the app-client load (OrganizationContext's appsError): its
   * own error, and a Retry that repeats that fetch. It renders in DataTable's
   * errorState slot, so the toolbar, count strip and any loaded rows stay
   * (ruling 19). Create stays hidden while the list is unknown.
   */
  const listError = hasListError(appsQuery) ? appsQuery.error : appsError ?? null;
  const retryList = () => {
    if (hasListError(appsQuery)) appsQuery.reset();
    if (appsError) void refreshOrganizationApps(organization.id, { includeCognitoDetails: true });
  };

  const emptyState = (
    <ListEmptyState
      icon={LayoutGrid}
      noun="app clients"
      search={appsQuery.search}
      onClearSearch={appsQuery.clearSearch}
      firstRun={{
        title: 'No app clients yet',
        description:
          'Create one to get a client ID and secret your integrations can authenticate with.',
        action: canWriteApps ? (
          <Button onClick={startCreatingApp}>
            <Plus aria-hidden="true" />
            Create app client
          </Button>
        ) : undefined,
      }}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="shrink-0">
      <PageHeader
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        actions={
          canWriteApps && !appsError ? (
            <Button onClick={startCreatingApp}>
              <Plus aria-hidden="true" />
              Create app client
            </Button>
          ) : undefined
        }
      />
      </div>

      {/*
        Create is a two-phase dialog, not a close-on-success one: the result
        phase stays on screen until the user dismisses it, so the credentials
        just minted are at hand to copy. It is also the success feedback — no
        toast fires with it. The secret stays retrievable afterwards from the
        app's details (GET /apps/{id}/credentials); only the bulk GET /apps
        strips it. Phase 1 is the form; phase 2 is the credentials it minted.
      */}
      <Dialog
        open={isCreating || createdApp !== null}
        pending={isSubmittingCreate}
        onOpenChange={(open) => {
          if (open) return;
          if (createdApp) setCreatedApp(null);
          else createGuard.onOpenChange(false);
        }}
      >
        <DialogContent>
          {createdApp ? (
            <>
              <DialogHeader>
                <DialogTitle>{createdApp.name} created</DialogTitle>
                <DialogDescription>
                  Copy the secret into your integration. You can read it again from the app
                  client's details at any time.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <CredentialField label="Client ID" value={createdApp.clientId} />
                {createdApp.clientSecret ? (
                  <CredentialField
                    label="Client secret"
                    value={createdApp.clientSecret}
                    secret
                    revealable={false}
                  />
                ) : null}
              </div>

              <DialogFooter>
                <Button onClick={() => setCreatedApp(null)}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            <DialogForm
              onSubmit={(event) => {
                event.preventDefault();
                void createApp();
              }}
            >
              <DialogHeader>
                <DialogTitle>Create app client</DialogTitle>
                <DialogDescription>
                  Creating an app client mints a client ID and secret your integration
                  authenticates with.
                </DialogDescription>
              </DialogHeader>

              <DialogBody>
                <Field label="App client name" error={createError} required>
                  {(control) => (
                    <Input
                      {...control}
                      value={newAppName}
                      autoComplete="off"
                      disabled={isSubmittingCreate}
                      onChange={(event) => {
                        setNewAppName(event.target.value);
                        setCreateError(null);
                        setCreateSubmitError(null);
                      }}
                    />
                  )}
                </Field>
              </DialogBody>

              <FormError message={createSubmitError} />
              <DialogActions>
                <Button
                  type="button"
                  variant="outline"
                  onClick={createGuard.requestClose}
                  disabled={isSubmittingCreate}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmittingCreate}>
                  Create app client
                </Button>
              </DialogActions>
            </DialogForm>
          )}
        </DialogContent>
      </Dialog>
      {createGuard.guardDialog}

        <DataTable
          {...listTableProps(appsQuery)}
          columns={columnVisibility.columns}
          rowKey={(app) => app.id}
          itemNoun="app clients"
          listLabel="App clients"
          searchable
          search={
            <SearchInput
              value={appsQuery.searchInput}
              onChange={appsQuery.setSearchInput}
              noun="app clients"
              placeholder="Search by name or client ID…"
              shortcut
            />
          }
          columnPicker={
            <ColumnPicker
              columns={allColumns}
              state={columnVisibility}
              sortGuard={{ activeSort: appsQuery.sort, onSortChange: appsQuery.onSortChange }}
            />
          }
          errorState={
            listError != null ? (
              <ErrorState
                title="Couldn't load app clients"
                error={listError}
                onRetry={retryList}
                isRetrying={isListRetrying(appsQuery) || appsLoading}
                action={appsQuery.search ? (
                  <Button variant="outline" size="sm" onClick={appsQuery.clearSearch}>Clear search</Button>
                ) : undefined}
              />
            ) : undefined
          }
          emptyState={emptyState}
          onOpenRow={(app) => openAppDetails(app, organization.id)}
          openLabel={(app) => app.name}
        />


      {selectedApp && (
        <AppDetailsModal
          // The count this page holds rides along, merged at render so a count
          // that lands after the dialog opened still reaches it: the dialog
          // needs it before its Subscriptions section is ever opened, to say
          // whether Delete app client would be refused.
          app={{
            ...selectedApp.app,
            subscriptionCount:
              subscriptionCounts?.get(selectedApp.app.id) ?? selectedApp.app.subscriptionCount,
          }}
          organizationId={selectedApp.organizationId}
          accessToken={accessToken || ''}
          onClose={() => setSelectedApp(null)}
          onDelete={handleAppDelete}
          canDeleteSubscriptions={hasPermission('subscriptions:delete')}
          canReadSecret={canWriteApps}
          canDelete={canDeleteApps}
          coverageLinks={{ lenses: hasPermission('lenses:read'), sensors: hasPermission('sensors:read') }}
          // Absent without app_clients:write, and for a template's publisher
          // credential: the API 409s a rename, because its name and its
          // lifetime belong to its template.
          onRename={!canWriteApps || selectedApp.app.clientKind === 'template_publisher'
            ? undefined
            : (name) => renameApp(selectedApp.app, name)}
        />
      )}
    </div>
  );
};

export default MyApps;
